/* eslint-disable max-lines-per-function */

import { connectIslands, EmptyIslandDefaults, EmptyObstructionDefaults, Island, newEmptyIsland, newEmptyObstruction, Panel } from '@rta/rta-blueprint-library';

import { AddSymbol, type SvgActionMenuConfig } from '@r-components';
import { ComponentController, DisplayMode } from '@r-core';

type Metadata = {
	controller: ComponentController;
	panel: Panel;
	connections: { isSource: boolean, isTarget: boolean };
};

export const addIslandMenuConfig = (island: Island, metadata: Metadata): SvgActionMenuConfig => {
	const { controller } = metadata;
	const baseRotation = 180 - (metadata.panel.getOriginAngle() - island.axis.y);
	const restrictAddPanel = controller.readonly || (metadata.connections.isSource || metadata.connections.isTarget || metadata.panel.isBackPanel);

	// inline
	const setInlineRotation = (newIsland: Island): Panel => {
		const sourcePanel = metadata.panel.isLeftPanel ? newIsland.panels.getFrontPanel().getRightPanel() : newIsland.panels.getFrontPanel().getLeftPanel();

		// Square the new island's end panel against the face it joins. Driven by the joined panels'
		// own origin angles, so a corner island's 45-degree side runs 45 degrees, not parallel to its front.
		const rotation = island.axis.y - metadata.panel.getOriginAngle() - sourcePanel.getOriginAngle();
		newIsland.axis.y = ((rotation % 360) + 360) % 360;

		return sourcePanel;
	}

	return {
		key: metadata.panel.id,
		icon: (props: React.SVGProps<SVGCircleElement>) => <AddSymbol {...props}/>,
		items: [
			{
			/* Inline */
				label: 'IB',
				tooltip: 'Inline Bar',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.bar, island.axis.y);
					const sourcePanel = setInlineRotation(newIsland);
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* Inline */
				label: 'IG',
				tooltip: 'Inline Grill',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.grill, island.axis.y);
					const sourcePanel = setInlineRotation(newIsland);
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* L-Shaped */
				label: 'LB',
				tooltip: 'L-Shaped Bar',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.bar, baseRotation);
					connectIslands({
						sourcePanel: newIsland.panels.getFrontPanel(),
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* L-Shaped */
				label: 'LG',
				tooltip: 'L-Shaped Grill',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.grill, baseRotation);
					connectIslands({
						sourcePanel: newIsland.panels.getFrontPanel(),
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* Corner */
				label: 'C',
				tooltip: 'Corner',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const rotation = baseRotation + ((metadata.panel as Panel).isLeftPanel ? 45 : -45);
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.corner, rotation);
					const sourcePanel = metadata.panel.isLeftPanel ? newIsland.panels.getFrontPanel().getRightPanel() : newIsland.panels.getFrontPanel().getLeftPanel();
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* Gally */
				label: 'G',
				tooltip: 'Galley',
				hidden: () => restrictAddPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.bar, baseRotation);
					newIsland.size = { width: metadata.panel.size.width };
					const sourcePanel = newIsland.panels.getFrontPanel();
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel,
						padding: 48
					});
				}
			},
			{
			/* Pizza Stand */
				label: 'IP',
				tooltip: 'Pizza Stand',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.pizza, island.axis.y);
					const sourcePanel = setInlineRotation(newIsland);
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel
					});
				}
			},
			{
			/* Asado Stand */
				label: 'IA',
				tooltip: 'Asado Stand',
				hidden: () => restrictAddPanel || metadata.panel.isFrontPanel,
				handler: () => {
					const newIsland = newEmptyIsland(island.refMap.kitchen, EmptyIslandDefaults.asado, island.axis.y);
					const sourcePanel = setInlineRotation(newIsland);
					connectIslands({
						sourcePanel,
						targetPanel: metadata.panel
					});
				}
			},
			{
				/* Edit Panel */
				label: 'P',
				// icon: (props) => <EditSymbol {...props}/>,
				tooltip: controller.readonly ? 'View Panel' : 'Edit Panel',
				hidden: () => false,
				handler: () => {
					controller.navigation.islandId = island.id;
					controller.navigation.panelId = metadata.panel.id;
					controller.navigation.displayMode = DisplayMode.Panel;
				}
			},
			{
			/* Wall */
				label: 'W',
				tooltip: 'Wall',
				hidden: () => false,
				handler: () => {
					const newObstruction = newEmptyObstruction(island.refMap.kitchen, EmptyObstructionDefaults.wall, baseRotation);
					const obstructionPanel = newObstruction.frontPanel;
					connectIslands({
						sourcePanel: obstructionPanel,
						targetPanel: metadata.panel,
						padding: 2.25,
						offset: -metadata.panel.getLeftPanel().countertopOverhang
					});
					const panelPoints = metadata.panel.getSidePoints({ includeCountertop: true, rotate: false });
					obstructionPanel.size.width = panelPoints.corners[1].x - panelPoints.corners[0].x;
					// console.log('wall-clicked', newObstruction);
				}
			},
			{
			/* Backsplash */
				label: 'BS',
				tooltip: 'Backsplash',
				hidden: () => false,
				handler: () => {
					// Create backsplash as a panel on the countertop
					const countertop = island.getCountertop();
					if (countertop) {
						countertop.createBacksplash(metadata.panel.id, { height: 4 });
					}
				}
			},
			{
			/* Utility Panel */
				label: 'UP',
				tooltip: 'Utility Panel',
				hidden: () => !(metadata.panel.isBackPanel || metadata.panel.isSidePanel),
				handler: () => {
					metadata.panel.addUtilityCutouts();
				}
			}
		]
	};
};
